import { Ok, Result } from 'ts-res';
import { KameleoonError } from 'src/kameleoonError/kameleoonError';
import { DataManager } from 'src/kameleoonData';
import { Requester } from 'src/requester';
import { SegmentType } from 'src/targeting';
import { ClientDataType } from 'src/storage';
import { IEventSource } from 'src/eventSource';
import {
  ClientConfigurationDataType,
  ClientConfigurationParametersType,
  ConfigurationDataType,
  ConfigurationType,
  ConsentBlockingBehaviour,
  ConsentType,
  ExperimentInfoType,
  ExperimentType,
  FeatureFlagType,
  MappedRuleType,
  ResultConfigurationDataType,
  RuleType,
  UpdateType,
} from './types';
import { IStorage } from 'src/storage/types';
import { IVisitorCodeManager } from 'src/visitorCodeManager';
import { EventManager, EventType } from 'src/eventManager';
import { IUrlProvider } from 'src/requester/urlProvider';
import { DEFAULT_DATA_FILE_CONFIGURATION } from './constants';
import { KameleoonLogger } from 'src/logging';
import { MEGroup } from 'src/clientConfiguration/meGroup';
import { Milliseconds } from 'src/types';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';

interface IClientConfiguration {
  initialize: () => Promise<Result<void, KameleoonError>>;
  readonly featureFlags: Map<string, FeatureFlagType>;
  readonly configuration: ConfigurationType;
  readonly segments: Map<string, SegmentType>;
  readonly ruleMap: Map<number, MappedRuleType>;
  readonly experimentMap: Map<number, ExperimentInfoType>;
  readonly holdout: ExperimentType | null;
  readonly meGroups: Map<string, MEGroup>;
}

export class ClientConfiguration implements IClientConfiguration {
  private updateConfigurationIntervalId: NodeJS.Timeout | null = null;
  private updateType: UpdateType = UpdateType.Polling;
  private configurationData: ClientConfigurationDataType =
    DEFAULT_DATA_FILE_CONFIGURATION;
  private featureFlagsData: Map<string, FeatureFlagType> = new Map();
  private isTargetedDeliveryRule: boolean | null = null;
  private segmentsData: Map<string, SegmentType> | null = null;
  private holdoutData: ExperimentType | null = null;
  private meGroupsData: Map<string, MEGroup> = new Map();
  private updateInterval: number;
  private lastUpdate: Date;
  private storage: IStorage<ClientDataType>;
  private requester: Requester;
  private eventSource: IEventSource;
  private mappedRules: Map<number, MappedRuleType> | null = null;
  private mappedExperiments: Map<number, ExperimentInfoType> | null = null;
  private dataManager: DataManager;
  private visitorCodeManager: IVisitorCodeManager;
  private eventManager: EventManager;
  private urlProvider: IUrlProvider;
  private lastModified?: string;
  private requestTime?: number;
  private externalPackageInfo: ExternalPackageInfoType;
  private usedDefaultDataFile: boolean = false;
  private defaultDataFile?: string;
  private blockingBehaviourMode: ConsentBlockingBehaviour =
    ConsentBlockingBehaviour.PartiallyBlocked;

  private readonly CACHE_REVALIDATE_PERIOD = 90 * Milliseconds.Minute;

  constructor({
    updateInterval,
    urlProvider,
    storage,
    requester,
    dataManager,
    eventSource,
    externalVisitorCodeManager,
    eventManager,
    externalPackageInfo,
    defaultDataFile,
  }: ClientConfigurationParametersType) {
    this.urlProvider = urlProvider;
    this.requester = requester;
    this.updateInterval = updateInterval;
    this.lastUpdate = new Date();
    this.storage = storage;
    this.dataManager = dataManager;
    this.eventManager = eventManager;
    this.visitorCodeManager = externalVisitorCodeManager;
    this.eventSource = eventSource;
    this.externalPackageInfo = externalPackageInfo;
    this.defaultDataFile = defaultDataFile;
  }

  public async initialize(): Promise<Result<void, KameleoonError>> {
    KameleoonLogger.debug('CALL: ClientConfiguration.initialize()');
    this.readStorageData();

    const shouldUpdate = this.checkShouldUpdate();

    if (shouldUpdate) {
      const updateResult = await this.updateClientConfiguration();

      if (!updateResult.ok) {
        KameleoonLogger.error(
          'Error occurred during client configuration update',
        );
        return updateResult;
      }

      // --- Note ---
      // If update type is being toggled `initialize` will be called again
      // so we don't need to continue execution here
      const isUpdateTypeToggled = updateResult.data;

      if (isUpdateTypeToggled) {
        KameleoonLogger.debug('RETURN: ClientConfiguration.initialize()');
        return Ok();
      }
    }

    if (this.updateType === UpdateType.RealTime && !this.eventSource.isOpen) {
      this.handleRealTimeUpdates();

      KameleoonLogger.debug('RETURN: ClientConfiguration.initialize()');
      return Ok();
    }

    if (this.updateType === UpdateType.Polling) {
      this.handlePollingUpdates();
    }

    KameleoonLogger.debug('RETURN: ClientConfiguration.initialize()');
    return Ok();
  }

  private cleanupHandlers(): void {
    if (this.eventSource.isOpen) {
      this.eventSource.close();
    }

    if (this.updateConfigurationIntervalId) {
      clearInterval(this.updateConfigurationIntervalId);
    }
  }

  private handleRealTimeUpdates(): void {
    this.cleanupHandlers();

    const updateEventCallback = (timestamp: number) => {
      this.updateClientConfiguration(timestamp).then((updateResult) => {
        if (updateResult.ok) {
          this.eventManager.fireEvent(EventType.ConfigurationUpdate, {
            timestamp,
          });
        }
      });
    };

    this.eventSource.open(updateEventCallback);
    this.eventSource.onError((error: Event) => {
      KameleoonLogger.error`Error occurred during real-time update: ${error}. Switching to polling updates while the connection is not restored.`;

      this.updateType = UpdateType.Polling;
      this.handlePollingUpdates();
    });
  }

  private handlePollingUpdates(): void {
    // --- Note ---
    // If `initialize` is called again the interval will be cleared
    // so theoretically we will constantly defer config update
    // but `this.lastUpdate` with `checkShouldUpdate` will prevent that
    this.cleanupHandlers();

    // --- Note ---
    // Error Handling is for `setInterval` emergency cleanup
    try {
      const lastUpdateTime = this.lastUpdate.getTime();
      const now = Date.now();
      const timeToNextUpdate = Math.max(
        this.updateInterval - (now - lastUpdateTime),
        0,
      );

      setTimeout(async () => {
        await this.updateClientConfiguration();
        this.updateConfigurationIntervalId = setInterval(
          this.updateClientConfiguration.bind(this),
          this.updateInterval,
        );
      }, timeToNextUpdate);
    } catch (err) {
      if (this.updateConfigurationIntervalId) {
        clearInterval(this.updateConfigurationIntervalId);
      }

      throw err;
    }
  }

  get featureFlags(): Map<string, FeatureFlagType> {
    return this.featureFlagsData;
  }

  get configuration(): ConfigurationType {
    return this.configurationData.configuration;
  }

  get segments(): Map<string, SegmentType> {
    if (this.segmentsData) {
      return this.segmentsData;
    }

    const segmentsData = new Map<string, SegmentType>();

    this.featureFlagsData.forEach((flag) => {
      flag.rules.forEach((rule) => {
        if (rule.segment && !segmentsData.has(rule.segment.id.toString())) {
          segmentsData.set(rule.segment.id.toString(), rule.segment);
        }
      });
    });

    this.segmentsData = segmentsData;

    return segmentsData;
  }

  get ruleMap(): Map<number, MappedRuleType> {
    if (this.mappedRules?.size) {
      return this.mappedRules;
    }

    const mappedRules = new Map<number, MappedRuleType>();

    for (const featureFlag of this.featureFlags.values()) {
      for (const rule of featureFlag.rules) {
        const { id, experimentId, variationByExposition } = rule;
        const variationMap = new Map<string, number>();

        variationByExposition.forEach(({ variationKey, variationId }) => {
          if (typeof variationId === 'number') {
            variationMap.set(variationKey, variationId);
          }
        });

        mappedRules.set(id, {
          featureId: featureFlag.id,
          experimentId,
          variations: variationMap,
        });
      }
    }

    this.mappedRules = mappedRules;

    return mappedRules;
  }

  get experimentMap(): Map<number, ExperimentInfoType> {
    if (this.mappedExperiments?.size) {
      return this.mappedExperiments;
    }

    const mappedExperiments = new Map<number, ExperimentInfoType>();

    for (const featureFlag of this.featureFlags.values()) {
      for (const rule of featureFlag.rules) {
        const { experimentId, variationByExposition } = rule;
        const variationMap = new Map<string, number>();

        variationByExposition.forEach(({ variationKey, variationId }) => {
          if (typeof variationId === 'number') {
            variationMap.set(variationKey, variationId);
          }
        });

        mappedExperiments.set(experimentId, {
          type: rule.type,
          variations: variationMap,
        });
      }
    }

    this.mappedExperiments = mappedExperiments;

    return mappedExperiments;
  }

  get holdout(): ExperimentType | null {
    return this.holdoutData;
  }

  get meGroups(): Map<string, MEGroup> {
    return this.meGroupsData;
  }

  get isConsentRequired(): boolean {
    return this.configuration.consentType === ConsentType.Required;
  }

  get consentBlockingBehaviour(): ConsentBlockingBehaviour {
    return this.blockingBehaviourMode;
  }

  get hasAnyTargetedDeliveryRule(): boolean {
    if (this.isTargetedDeliveryRule !== null) {
      return this.isTargetedDeliveryRule;
    }

    for (const featureFlag of this.featureFlags.values()) {
      if (
        featureFlag.environmentEnabled &&
        featureFlag.rules.some(
          (rule) => rule.type === RuleType.TARGETED_DELIVERY,
        )
      ) {
        return true;
      }
    }

    return false;
  }

  private checkShouldUpdate(): boolean {
    const { configuration } = this.configurationData;

    this.updateType = configuration.realTimeUpdate
      ? UpdateType.RealTime
      : UpdateType.Polling;

    if (!this.featureFlags.size) {
      return true;
    }

    const lastUpdateTime = this.lastUpdate.getTime();
    const updateWindowEdge = Date.now() - this.updateInterval;

    return lastUpdateTime < updateWindowEdge;
  }

  private readStorageData(): void {
    KameleoonLogger.debug('CALL: ClientConfiguration.readStorageData()');
    const result = this.storage.read();

    if (result.ok) {
      let { data, lastUpdate, lastModified, requestTime } = result.data;

      let updateFeatureFlagsInfo = false;
      if (this.defaultDataFile) {
        try {
          const defaultDataFile = JSON.parse(
            this.defaultDataFile,
          ) as ConfigurationDataType;
          this.defaultDataFile = undefined;
          this.usedDefaultDataFile = true;
          const localDateModified = data.dateModified ?? requestTime ?? -1;
          const defaultDateModified = defaultDataFile.dateModified ?? 0;
          if (!requestTime || defaultDateModified > localDateModified) {
            data = defaultDataFile;
            updateFeatureFlagsInfo = true;
          }
        } catch (error) {
          KameleoonLogger.error`Error parsing default data file: ${String(
            error,
          )}`;
        }
      }

      this.updateConfigurationData(data, updateFeatureFlagsInfo);

      this.lastUpdate = this.usedDefaultDataFile
        ? new Date(0)
        : new Date(Date.parse(lastUpdate));
      this.lastModified = lastModified;
      this.requestTime = requestTime;
    }
    KameleoonLogger.debug`RETURN: ClientConfiguration.readStorageData() -> (result: ${result.ok})`;
  }

  /**
   * Updates client configuration
   *
   * @param timeStamp - timestamp for real-time configuration update
   *
   * @returns `true` if current update type should be toggled (SSE/Polling)
   */
  private async updateClientConfiguration(
    timestamp?: number,
  ): Promise<Result<boolean, KameleoonError>> {
    const currentTime = Date.now();

    if (this.usedDefaultDataFile) {
      this.updateClientConfigurationInBackground();
      return Ok(false);
    }

    if (this.requestTime && !timestamp && !this.externalPackageInfo.isServer) {
      if (currentTime - this.requestTime <= this.CACHE_REVALIDATE_PERIOD) {
        this.updateClientConfigurationInBackground();
        return Ok(false);
      } else {
        KameleoonLogger.debug`Cached configuration data has expired. Fetching fresh data.`;
      }
    }

    const clientConfigurationResult =
      await this.requester.getClientConfiguration(timestamp, this.lastModified);

    return this.applyNewConfigurationDataFromResponse(
      clientConfigurationResult,
    );
  }

  private updateClientConfigurationInBackground() {
    setTimeout(async () => {
      const clientConfigurationResult =
        await this.requester.getClientConfiguration(
          undefined,
          this.lastModified,
        );
      await this.applyNewConfigurationDataFromResponse(
        clientConfigurationResult,
      );
    }, 0);
  }

  private async applyNewConfigurationDataFromResponse(
    clientConfigurationResult: Result<
      ResultConfigurationDataType,
      KameleoonError
    >,
  ): Promise<Result<boolean, KameleoonError>> {
    this.requestTime = Date.now();
    if (!clientConfigurationResult.ok) {
      if (this.featureFlags.size) {
        KameleoonLogger.error(
          'Error occurred while updating configuration. Existing configuration will be used as a fallback.',
        );
        return Ok(true);
      }

      return clientConfigurationResult;
    }

    if (clientConfigurationResult.data.configuration === undefined) {
      // If configuration is undefined, it means that the server returned 304 Not Modified
      // and we should not update the configuration, but need to update the lastUpdate in storage
      this.updateStorageData();
      return Ok(false);
    }

    const clientConfigurationData =
      clientConfigurationResult.data.configuration;

    this.updateConfigurationData(clientConfigurationData, true);

    this.dataManager.clearTrees();
    this.mappedRules?.clear();
    this.experimentMap?.clear();
    this.isTargetedDeliveryRule = null;

    if (clientConfigurationResult.data.lastModified) {
      this.lastModified = clientConfigurationResult.data.lastModified;
    }

    const result = this.updateStorageData();

    if (!result.ok) {
      return result;
    }

    const updateType = clientConfigurationData.configuration.realTimeUpdate
      ? UpdateType.RealTime
      : UpdateType.Polling;

    const toggleUpdateType = updateType !== this.updateType;

    if (toggleUpdateType) {
      this.updateType = updateType;
      KameleoonLogger.info`Configuration update type was toggled to ${UpdateType[updateType]}`;
      await this.initialize();
    }

    this.blockingBehaviourMode = this.consentBlockingBehaviourFromStr(
      clientConfigurationData.configuration.consentOptOutBehavior,
    );

    return Ok(toggleUpdateType);
  }

  private updateConfigurationData(
    data: ConfigurationDataType,
    updateFeatureFlagsInfo: boolean = true,
  ): void {
    const {
      configuration,
      featureFlags,
      customData,
      holdout,
      segments,
      dateModified,
    } = data;

    let segmentsMap: Map<string, SegmentType> | null = segments
      ? new Map(segments.map((segment) => [segment.id.toString(), segment]))
      : null;
    let customDataIndexById: Map<number, number> | null = null;

    if (updateFeatureFlagsInfo) {
      if (customData?.length) {
        customDataIndexById = new Map();
        for (let data of customData) {
          if (data.id !== undefined) {
            customDataIndexById.set(data.id, data.index);
          } else {
            customDataIndexById = null;
            break;
          }
        }
      }
    }

    const featureFlagsMap: Map<string, FeatureFlagType> = new Map();
    for (const featureFlag of featureFlags) {
      featureFlagsMap.set(featureFlag.featureKey, featureFlag);

      // configurationFeatureFlag data from storage not needed to be modified
      if (updateFeatureFlagsInfo) {
        this.updateFeatureFlagSegments(featureFlag, segmentsMap);
        this.updateFeatureFlagBucketingCustomDataId(
          featureFlag,
          customDataIndexById,
        );
      }
    }
    this.featureFlagsData = featureFlagsMap;

    if (customData?.length) {
      this.dataManager.customDataIndexes = customData;
    }

    this.meGroupsData = this.makeMEGroups(featureFlags);
    this.holdoutData = holdout ?? null;

    this.configurationData = {
      configuration,
      customData,
      holdout,
      dateModified,
    };

    this.urlProvider.dataApiDomain = data.configuration.dataApiDomain;

    this.updateConsentRequired();
    this.segmentsData = segmentsMap;
  }

  private updateFeatureFlagSegments(
    featureFlag: FeatureFlagType,
    segmentsMap: Map<string, SegmentType> | null,
  ): void {
    if (segmentsMap !== null) {
      featureFlag.rules.forEach((rule) => {
        const segmentId = rule.segmentId;
        rule.segment =
          segmentId != null
            ? segmentsMap.get(segmentId.toString()) ?? null
            : null;
      });
    }
  }

  private updateFeatureFlagBucketingCustomDataId(
    featureFlag: FeatureFlagType,
    customDataIndexById: Map<number, number> | null,
  ): void {
    const bucketingCustomDataId = featureFlag.bucketingCustomDataId;
    if (bucketingCustomDataId != null && customDataIndexById) {
      featureFlag.bucketingCustomDataIndex = customDataIndexById.get(
        bucketingCustomDataId,
      );
    }
  }

  private makeMEGroups(featureFlags: FeatureFlagType[]): Map<string, MEGroup> {
    const meGroupLists = new Map<string, FeatureFlagType[]>();

    featureFlags.forEach((featureFlag) => {
      const groupName = featureFlag.mutuallyExclusiveGroup;
      if (groupName) {
        let meGroupList = meGroupLists.get(groupName);
        if (meGroupList) {
          meGroupList.push(featureFlag);
        } else {
          meGroupLists.set(groupName, [featureFlag]);
        }
      }
    });

    const meGroups = new Map<string, MEGroup>();

    meGroupLists.forEach((featureFlags, groupName) => {
      meGroups.set(groupName, new MEGroup(featureFlags));
    });

    return meGroups;
  }

  private updateStorageData(): Result<void, KameleoonError> {
    this.lastUpdate = new Date();

    const configurationData = {
      ...this.configurationData,
      featureFlags: Array.from(this.featureFlags.values()),
      segments: this.segmentsData
        ? Array.from(this.segmentsData.values())
        : undefined,
    };

    const data: ClientDataType = {
      data: configurationData,
      lastUpdate: this.lastUpdate.toString(),
      lastModified: this.lastModified,
      requestTime: this.requestTime,
    };

    return this.storage.write(data);
  }

  private updateConsentRequired(): void {
    // --- Note ---
    // According to GDPR policy we should set visitorCode if legal consent isn't required
    // or we have at least one Targeted Delivery rule in configuration
    this.visitorCodeManager.consentRequired =
      this.isConsentRequired && !this.hasAnyTargetedDeliveryRule;
  }

  private consentBlockingBehaviourFromStr(
    str: string,
  ): ConsentBlockingBehaviour {
    if (
      str === ConsentBlockingBehaviour.PartiallyBlocked ||
      str === ConsentBlockingBehaviour.CompletelyBlocked
    ) {
      return str;
    }

    KameleoonLogger.error(`Unexpected consent blocking type '${str}'`);
    return ConsentBlockingBehaviour.PartiallyBlocked;
  }
}
